"use client";

import { useMemo, useState } from "react";
import {
  COMPATIBILITY_LEVEL_HI,
  RASHIS,
  RASHI_EMOJIS,
  RASHI_MAP,
  getCompatibilityScore,
  getCompatibilityColor,
  getCompatibilityLevel,
  getKootaBreakdown,
  getDimensionScores,
  type Rashi,
  type KootaResult,
} from "@/lib/rashi";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import type { CustomerLanguage } from "@/lib/customer-language";
import { elementNameHi, planetNameHi } from "@/lib/vedic-names-hi";

interface Props {
  initialSign1?: string;
  initialSign2?: string;
  /** A Hindi page names the signs and sections in Hindi and links Hindi pages. */
  language?: CustomerLanguage;
}

const HEADINGS = {
  en: {
    dimensions: "Core Compatibility Dimensions",
    kootas: "Vedic Gun Milan (Rashi-Computable Kootas — Max 15 Points)",
  },
  hi: {
    dimensions: "अनुकूलता के मुख्य आयाम",
    kootas: "वैदिक गुण मिलान (राशि से गणना योग्य कूट — अधिकतम 15 अंक)",
  },
} as const;

/** Short tool labels; the explanatory sentences stay in English. */
const LABELS = {
  en: {
    sign1: "First Rashi (Moon / Sun Sign)",
    sign2: "Second Rashi (Partner's Sign)",
    swapAria: "Swap signs",
    swapTitle: "Swap Signs",
    matchScore: "Match Score",
    match: "Match",
    emotional: "Emotional Harmony",
    communication: "Communication & Intellect",
    physical: "Physical & Energy Chemistry",
    family: "Family & Long-Term Values",
    points: "pts",
    elemental: "Elemental Harmony",
    rulers: "Ruling Planets",
    ask: "Ask AI Astrologer About This Match ✦",
  },
  hi: {
    sign1: "पहली राशि (चंद्र / सूर्य राशि)",
    sign2: "दूसरी राशि (साथी की राशि)",
    swapAria: "राशियाँ बदलें",
    swapTitle: "राशियाँ बदलें",
    matchScore: "मिलान स्कोर",
    match: "मिलान",
    emotional: "भावनात्मक सामंजस्य",
    communication: "संवाद और बुद्धि",
    physical: "शारीरिक और ऊर्जा आकर्षण",
    family: "परिवार और दीर्घकालिक मूल्य",
    points: "अंक",
    elemental: "तत्व सामंजस्य",
    rulers: "स्वामी ग्रह",
    ask: "इस मिलान पर AI ज्योतिषी से पूछें ✦",
  },
} as const;

const ELEMENT_DESCRIPTIONS: Record<string, string> = {
  "fire-fire":
    "High energy, immense passion, and mutual ambition. Both need conscious patience to avoid ego clashes.",
  "earth-earth":
    "Deeply grounded, stable, and practical partnership with shared long-term values and financial security.",
  "air-air":
    "Intellectually stimulating, communicative, and socially vibrant, though may require effort for emotional grounding.",
  "water-water":
    "Deeply intuitive, empathetic, and emotionally attuned. Highly romantic and supportive bond.",
  "earth-water":
    "Nurturing and complementary: Earth provides structure, while Water provides emotional warmth.",
  "air-fire":
    "Dynamic and inspiring: Air fuels Fire's vision, creating enthusiasm, creativity, and shared adventure.",
  "fire-water":
    "Intense emotional attraction that requires understanding: Fire's directness balances Water's sensitivity.",
  "earth-fire":
    "Productive and goal-oriented: Earth's discipline helps anchor Fire's passionate drive into real results.",
  "air-earth":
    "Practical intellect meets pragmatic action: Communication bridges differing emotional styles.",
  "air-water":
    "Thought meets emotion: Requires active listening to blend intellectual clarity with feeling.",
};

function getInitialSign(paramKey: string, fallback: string): string {
  if (typeof window !== "undefined") {
    const p = new URLSearchParams(window.location.search).get(paramKey)?.toLowerCase();
    if (p && RASHI_MAP[p]) return p;
  }
  return fallback;
}

export default function CompatibilityStudio({
  initialSign1 = "aries",
  initialSign2 = "leo",
  language = "en",
}: Props) {
  const [sign1Id, setSign1Id] = useState<string>(() => getInitialSign("sign1", initialSign1));
  const [sign2Id, setSign2Id] = useState<string>(() => getInitialSign("sign2", initialSign2));

  const rashi1 = useMemo<Rashi>(() => RASHI_MAP[sign1Id] || RASHIS[0], [sign1Id]);
  const rashi2 = useMemo<Rashi>(() => RASHI_MAP[sign2Id] || RASHIS[4], [sign2Id]);

  const score = useMemo(() => getCompatibilityScore(rashi1, rashi2), [rashi1, rashi2]);
  const level = useMemo(() => getCompatibilityLevel(score), [score]);
  const color = useMemo(() => getCompatibilityColor(score), [score]);
  const kootas = useMemo(() => getKootaBreakdown(rashi1, rashi2), [rashi1, rashi2]);
  const dimensions = useMemo(() => getDimensionScores(rashi1, rashi2), [rashi1, rashi2]);

  const handleSwap = () => {
    setSign1Id(sign2Id);
    setSign2Id(sign1Id);
    updateQueryParams(sign2Id, sign1Id);
  };

  const handleSign1Change = (id: string) => {
    setSign1Id(id);
    updateQueryParams(id, sign2Id);
  };

  const handleSign2Change = (id: string) => {
    setSign2Id(id);
    updateQueryParams(sign1Id, id);
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm">
      <CompatibilitySelectors
        sign1Id={sign1Id}
        sign2Id={sign2Id}
        onSign1Change={handleSign1Change}
        onSign2Change={handleSign2Change}
        onSwap={handleSwap}
        language={language}
      />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <CompatibilityScoreCard
          rashi1={rashi1}
          rashi2={rashi2}
          score={score}
          level={level}
          color={color}
          language={language}
        />
        <CompatibilityDimensionGauges dimensions={dimensions} language={language} />
      </div>

      <CompatibilityKootaGrid kootas={kootas.kootas} language={language} />
      <CompatibilityElementalDynamics rashi1={rashi1} rashi2={rashi2} language={language} />
      <CompatibilityActions rashi1={rashi1} rashi2={rashi2} language={language} />
    </div>
  );
}

function updateQueryParams(s1: string, s2: string) {
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    url.searchParams.set("sign1", s1);
    url.searchParams.set("sign2", s2);
    window.history.replaceState({}, "", url.toString());
  }
}

function CompatibilitySelectors({
  sign1Id,
  sign2Id,
  onSign1Change,
  onSign2Change,
  onSwap,
  language,
}: {
  sign1Id: string;
  sign2Id: string;
  onSign1Change: (id: string) => void;
  onSign2Change: (id: string) => void;
  onSwap: () => void;
  language: CustomerLanguage;
}) {
  const labels = LABELS[language];
  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 pb-6 border-b border-border-default">
      <SignDropdown
        id="sign1-select"
        label={labels.sign1}
        value={sign1Id}
        onChange={onSign1Change}
        language={language}
      />
      <button
        type="button"
        onClick={onSwap}
        aria-label={labels.swapAria}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border-default bg-surface-raised text-body font-bold text-text-primary hover:bg-surface-brand-subtle hover:text-intent-brand transition-colors"
        title={labels.swapTitle}
      >
        ⇄
      </button>
      <SignDropdown
        id="sign2-select"
        label={labels.sign2}
        value={sign2Id}
        onChange={onSign2Change}
        language={language}
      />
    </div>
  );
}

function SignDropdown({
  id,
  label,
  value,
  onChange,
  language,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (id: string) => void;
  language: CustomerLanguage;
}) {
  return (
    <div className="w-full md:w-[42%]">
      <label
        htmlFor={id}
        className="block text-caption font-bold uppercase tracking-wider text-text-muted mb-2"
      >
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-xl border border-border-default bg-surface-raised px-4 py-3 text-body-small font-semibold text-text-primary focus:border-border-interactive focus:outline-none focus:ring-2 focus:ring-intent-brand pr-10"
        >
          {RASHIS.map((r) => (
            <option key={r.id} value={r.id}>
              {language === "hi"
                ? `${RASHI_EMOJIS[r.id]} ${r.name_hi} — ${elementNameHi(r.element) ?? r.element}`
                : `${RASHI_EMOJIS[r.id]} ${r.name_en} (${r.name_hi}) — ${r.element.toUpperCase()}`}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-text-muted text-caption">
          ▼
        </span>
      </div>
    </div>
  );
}

function CompatibilityScoreCard({
  rashi1,
  rashi2,
  score,
  level,
  color,
  language,
}: {
  rashi1: Rashi;
  rashi2: Rashi;
  score: number;
  level: string;
  color: string;
  language: CustomerLanguage;
}) {
  const labels = LABELS[language];
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="lg:col-span-5 flex flex-col items-center justify-center text-center p-6 rounded-2xl bg-surface-brand-subtle border border-border-default">
      <div className="relative flex items-center justify-center w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r={radius}
            className="stroke-border-default fill-none"
            strokeWidth="8"
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-3xl font-bold font-serif text-text-primary">{score}%</span>
          <span className="text-caption font-semibold uppercase tracking-wider text-text-muted">
            {labels.matchScore}
          </span>
        </div>
      </div>

      <div className="mt-4">
        <span
          className={`inline-block px-3.5 py-1 rounded-full text-caption font-bold ${
            score >= 28
              ? "bg-success/10 text-success"
              : score >= 21
                ? "bg-intent-brand/10 text-intent-brand"
                : score >= 14
                  ? "bg-warning/10 text-warning"
                  : "bg-error/10 text-error"
          }`}
        >
          {language === "hi" ? (COMPATIBILITY_LEVEL_HI[level] ?? level) : level} {labels.match}
        </span>
        <h2 className="mt-2 text-title font-bold text-text-primary">
          {language === "hi"
            ? `${rashi1.name_hi} और ${rashi2.name_hi}`
            : `${rashi1.name_en} & ${rashi2.name_en}`}
        </h2>
        <p className="text-caption text-text-secondary mt-1">
          {rashi1.name_hi} ({rashi1.symbol}) + {rashi2.name_hi} ({rashi2.symbol})
        </p>
      </div>
    </div>
  );
}

function CompatibilityDimensionGauges({
  dimensions,
  language,
}: {
  dimensions: ReturnType<typeof getDimensionScores>;
  language: CustomerLanguage;
}) {
  return (
    <div className="lg:col-span-7 space-y-4">
      <h3 className="text-body-small font-bold uppercase tracking-wider text-text-primary mb-3">
        {HEADINGS[language].dimensions}
      </h3>
      <DimensionBar label={LABELS[language].emotional} value={dimensions.emotional} />
      <DimensionBar label={LABELS[language].communication} value={dimensions.communication} />
      <DimensionBar label={LABELS[language].physical} value={dimensions.physical} />
      <DimensionBar label={LABELS[language].family} value={dimensions.family} />
    </div>
  );
}

function getWidthClass(val: number): string {
  if (val >= 95) return "w-full";
  if (val >= 85) return "w-11/12";
  if (val >= 75) return "w-3/4";
  if (val >= 60) return "w-3/5";
  if (val >= 50) return "w-1/2";
  if (val >= 40) return "w-2/5";
  if (val >= 25) return "w-1/4";
  return "w-1/6";
}

function DimensionBar({ label, value }: { label: string; value: number }) {
  const barColor =
    value >= 75
      ? "bg-success"
      : value >= 55
        ? "bg-intent-brand"
        : value >= 40
          ? "bg-warning"
          : "bg-error";
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-caption">
        <span className="font-semibold text-text-primary">{label}</span>
        <span className="font-bold text-text-secondary">{value}%</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-surface-warm overflow-hidden">
        <div
          className={`h-full rounded-full ${barColor} ${getWidthClass(value)} transition-all duration-500`}
        />
      </div>
    </div>
  );
}

function CompatibilityKootaGrid({
  kootas,
  language,
}: {
  kootas: KootaResult[];
  language: CustomerLanguage;
}) {
  return (
    <div className="mt-8 pt-8 border-t border-border-default">
      <h3 className="text-body-small font-bold uppercase tracking-wider text-text-primary mb-4">
        {HEADINGS[language].kootas}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kootas.map((k) => (
          <div
            key={k.name}
            className="p-4 rounded-xl border border-border-default bg-surface-raised flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-body-small font-bold text-text-primary">
                  {language === "hi" ? k.name_hi : k.name}
                </span>
                <span className="text-caption font-semibold text-intent-brand">
                  {k.score} / {k.max} {LABELS[language].points}
                </span>
              </div>
              {language === "hi" ? null : (
                <span className="text-eyebrow text-text-muted">{k.name_hi}</span>
              )}
              <p className="mt-2 text-caption text-text-secondary leading-relaxed">
                {k.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompatibilityElementalDynamics({
  rashi1,
  rashi2,
  language,
}: {
  rashi1: Rashi;
  rashi2: Rashi;
  language: CustomerLanguage;
}) {
  const labels = LABELS[language];
  const elemKey = [rashi1.element, rashi2.element].sort().join("-");
  const desc =
    ELEMENT_DESCRIPTIONS[elemKey] ??
    "A unique blend of planetary energies with distinct growth opportunities.";

  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="p-4 rounded-xl border border-border-default bg-surface-raised">
        <p className="text-eyebrow font-bold uppercase tracking-wider text-text-muted">
          {labels.elemental}
        </p>
        <p className="mt-1 text-body-small font-bold text-text-primary capitalize">
          {language === "hi"
            ? `${elementNameHi(rashi1.element) ?? rashi1.element} + ${elementNameHi(rashi2.element) ?? rashi2.element}`
            : `${rashi1.element} + ${rashi2.element}`}
        </p>
        <p className="mt-2 text-caption text-text-secondary leading-relaxed">{desc}</p>
      </div>

      <div className="p-4 rounded-xl border border-border-default bg-surface-raised">
        <p className="text-eyebrow font-bold uppercase tracking-wider text-text-muted">
          {labels.rulers}
        </p>
        <p className="mt-1 text-body-small font-bold text-text-primary capitalize">
          {language === "hi"
            ? `${planetNameHi(rashi1.ruler) ?? rashi1.ruler} (${rashi1.name_hi}) और ${planetNameHi(rashi2.ruler) ?? rashi2.ruler} (${rashi2.name_hi})`
            : `${rashi1.ruler} (${rashi1.name_en}) & ${rashi2.ruler} (${rashi2.name_en})`}
        </p>
        <p className="mt-2 text-caption text-text-secondary leading-relaxed">
          Astrological relationship between planetary lords determines instinctive communication and
          mutual understanding.
        </p>
      </div>
    </div>
  );
}

function CompatibilityActions({
  rashi1,
  rashi2,
  language,
}: {
  rashi1: Rashi;
  rashi2: Rashi;
  language: CustomerLanguage;
}) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/compatibility"),
    "/compatibility",
    language
  );

  const handleConsultClick = () => {
    const prompt = `How compatible are ${rashi1.name_en} (${rashi1.name_hi}) and ${rashi2.name_en} (${rashi2.name_hi}) for relationship and marriage? What are the key strengths and potential doshas?`;
    try {
      safeSessionSet("seo_entry_intent", "generic");
      safeSessionSet("seo_entry_path", "/compatibility");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border-default">
      <a
        href={chatHref}
        onClick={handleConsultClick}
        className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-surface-brand-strong px-6 py-3 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
      >
        {LABELS[language].ask}
      </a>
    </div>
  );
}
